
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { tenantService, communicationService } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';

interface MessageTemplate {
    id: string;
    name: string;
    type: 'email' | 'sms' | 'letter';
    subject?: string;
    content: string;
    category: 'rent' | 'maintenance' | 'compliance' | 'notice' | 'general';
}

interface SentMessage {
    id: string;
    templateId?: string;
    recipientName: string;
    recipientEmail: string;
    type: 'email' | 'sms' | 'letter';
    subject: string;
    sentAt: string;
    status: 'sent' | 'delivered' | 'failed';
}

// Pre-built templates for common landlord communications
const DEFAULT_TEMPLATES: MessageTemplate[] = [
    {
        id: 'rent-reminder',
        name: 'Rent Reminder',
        type: 'email',
        subject: 'Rent Payment Reminder',
        content: 'Dear {{tenant_name}},\n\nThis is a friendly reminder that your rent payment of £{{rent_amount}} is due on {{due_date}}.\n\nPlease ensure payment is made by this date to avoid any late fees.\n\nBest regards,\n{{landlord_name}}',
        category: 'rent'
    },
    {
        id: 'maintenance-scheduled',
        name: 'Maintenance Visit Scheduled',
        type: 'email',
        subject: 'Scheduled Maintenance Visit',
        content: 'Dear {{tenant_name}},\n\nWe have scheduled a maintenance visit to your property at {{property_address}} on {{date}} at {{time}}.\n\nPlease ensure access is available. If this time is not convenient, please contact us to rearrange.\n\nBest regards,\n{{landlord_name}}',
        category: 'maintenance'
    },
    {
        id: 'gas-safety-check',
        name: 'Gas Safety Check Notice',
        type: 'email',
        subject: 'Annual Gas Safety Check - Access Required',
        content: 'Dear {{tenant_name}},\n\nAs required by law, we need to conduct an annual Gas Safety check at {{property_address}}.\n\nThe inspection is scheduled for {{date}} between {{time_slot}}. A Gas Safe registered engineer will attend.\n\nPlease ensure someone is available to provide access.\n\nBest regards,\n{{landlord_name}}',
        category: 'compliance'
    },
    {
        id: 'ground-8-notice',
        name: 'Section 8 Notice (Ground 8 - Serious Arrears)',
        type: 'letter',
        subject: 'Notice to Quit - Ground 8 (Serious Rent Arrears)',
        content: 'IMPORTANT LEGAL NOTICE - GROUND 8 NOTICE TO QUIT\n\nDear {{tenant_name}},\n\nRE: {{property_address}}\n\nThis is a formal notice to quit the above property under Ground 8 (Serious Rent Arrears) of the Renters\' Rights Act 2025.\n\nREASSON FOR NOTICE:\nAs of {{date}}, rent arrears totaling £{{rent_amount}} are outstanding, representing {{weeks_outstanding}} weeks of rent.\n\nNOTICE PERIOD:\nYou are required to vacate the property by {{notice_date}} (at least 28 days from the date of service).\n\nACTION REQUIRED:\nPlease make full payment of the outstanding arrears (£{{rent_amount}}) immediately to prevent escalation to possession proceedings. Alternatively, contact us within 7 days to propose a payment arrangement.\n\nFAILURE TO COMPLY:\nFailure to vacate by the date specified or pay the arrears may result in court possession proceedings at your cost.\n\nThis notice is served in accordance with the Renters\' Rights Act 2025 and the Housing Act 1988.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'arrears-notice',
        name: 'Formal Arrears Notice',
        type: 'email',
        subject: 'FORMAL NOTICE: Rent Arrears at {{property_address}}',
        content: 'Dear {{tenant_name}},\n\nThis is a formal notice regarding unpaid rent for your tenancy at {{property_address}}.\n\nAs of {{date}}, your rent account is in arrears by £{{rent_amount}}.\n\nThis is a serious breach of your tenancy agreement. Please make payment immediately or contact us to discuss a payment plan. Failure to resolve this may lead to possession proceedings under the Renters\' Rights Act 2025.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'breach-notice',
        name: 'Tenancy Breach Notice',
        type: 'email',
        subject: 'Notice of Breach of Tenancy Agreement',
        content: 'Dear {{tenant_name}},\n\nWe are writing to formally notify you of a breach of your tenancy agreement at {{property_address}}.\n\nSpecifically: [Enter Details of Breach]\n\nYou are required to remedy this breach by [Enter Date]. Failure to do so may result in further action, including the service of a notice seeking possession.\n\nPlease contact us immediately to discuss this matter.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'welcome-tenant',
        name: 'Welcome New Tenant',
        type: 'email',
        subject: 'Welcome to Your New Home!',
        content: 'Dear {{tenant_name}},\n\nWelcome to {{property_address}}!\n\nWe hope you enjoy your new home. Please find attached:\n- Property inventory\n- Emergency contact information\n- Utility transfer details\n\nIf you have any questions, please don\'t hesitate to contact us.\n\nBest regards,\n{{landlord_name}}',
        category: 'general'
    }
];

/**
 * F-308 — Texas template set. The UK defaults above cite the Renters' Rights
 * Act 2025, Housing Act 1988, Ground 8 / Section 8 and £ amounts; none of
 * that may be offered to a Texas landlord. Texas gets its own set built on
 * Property Code notice practice, $ amounts and HVAC readiness.
 */
export const TEXAS_TEMPLATES: MessageTemplate[] = [
    {
        id: 'rent-reminder',
        name: 'Rent Reminder',
        type: 'email',
        subject: 'Rent Payment Reminder',
        // eslint-disable-next-line no-template-curly-in-string -- '$' + '{{mustache}}' is intentional
        content: 'Dear {{tenant_name}},\n\nThis is a friendly reminder that your rent payment of ${{rent_amount}} is due on {{due_date}}.\n\nPlease ensure payment is made by this date to avoid any late fees.\n\nBest regards,\n{{landlord_name}}',
        category: 'rent'
    },
    {
        id: 'maintenance-scheduled',
        name: 'Maintenance Visit Scheduled',
        type: 'email',
        subject: 'Scheduled Maintenance Visit',
        content: 'Dear {{tenant_name}},\n\nWe have scheduled a maintenance visit to your property at {{property_address}} on {{date}} at {{time}}.\n\nPlease ensure access is available. If this time is not convenient, please contact us to rearrange.\n\nBest regards,\n{{landlord_name}}',
        category: 'maintenance'
    },
    {
        id: 'hvac-service-check',
        name: 'HVAC / A/C Service Notice',
        type: 'email',
        subject: 'HVAC Service Appointment - Access Required',
        content: 'Dear {{tenant_name}},\n\nTo keep the property ready for North Texas heat, we need to service the heating and air-conditioning system at {{property_address}}.\n\nThe appointment is scheduled for {{date}} between {{time_slot}}. Please ensure someone is able to provide access.\n\nBest regards,\n{{landlord_name}}',
        category: 'compliance'
    },
    {
        id: 'notice-to-vacate',
        name: 'Texas Notice to Vacate (3-Day)',
        type: 'letter',
        subject: 'Notice to Vacate - {{property_address}}',
        // eslint-disable-next-line no-template-curly-in-string -- '$' + '{{mustache}}' is intentional
        content: 'NOTICE TO VACATE\n\nDear {{tenant_name}},\n\nRE: {{property_address}}\n\nYou are notified to vacate the premises. Rent of ${{rent_amount}} is outstanding as of {{date}}.\n\nYou have three (3) days from the date this notice is served to vacate the premises or to pay all past-due rent, as provided by Texas Property Code Section 24.005.\n\nIf you fail to vacate or pay within that period, a forcible detainer suit may be filed in the Justice Court.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'arrears-notice',
        name: 'Formal Rent Arrears Notice',
        type: 'email',
        subject: 'FORMAL NOTICE: Rent Arrears at {{property_address}}',
        // eslint-disable-next-line no-template-curly-in-string -- '$' + '{{mustache}}' is intentional
        content: 'Dear {{tenant_name}},\n\nThis is a formal notice regarding unpaid rent for your tenancy at {{property_address}}.\n\nAs of {{date}}, rent of ${{rent_amount}} remains unpaid.\n\nPlease remit payment immediately or contact us to discuss a payment plan. Failure to resolve this may result in a notice to vacate and, if necessary, filing in Justice Court.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'breach-notice',
        name: 'Lease Violation Notice',
        type: 'email',
        subject: 'Notice of Lease Violation',
        content: 'Dear {{tenant_name}},\n\nWe are writing to formally notify you of a violation of your lease at {{property_address}}.\n\nSpecifically: [Enter Details of Violation]\n\nYou are required to remedy this violation by [Enter Date]. Failure to do so may result in a notice to vacate.\n\nPlease contact us immediately to discuss this matter.\n\nBest regards,\n{{landlord_name}}',
        category: 'notice'
    },
    {
        id: 'welcome-tenant',
        name: 'Welcome New Tenant',
        type: 'email',
        subject: 'Welcome to Your New Home!',
        content: 'Dear {{tenant_name}},\n\nWelcome to {{property_address}}!\n\nWe hope you enjoy your new home. Please find attached:\n- Lease agreement\n- Emergency contact information\n- Move-in condition report\n\nIf you have any questions, please don\'t hesitate to contact us.\n\nBest regards,\n{{landlord_name}}',
        category: 'general'
    }
];

/** F-308: templates are market-specific — Texas never sees the UK set. */
export const getMarketTemplates = (market: string): MessageTemplate[] =>
    market === 'texas' ? TEXAS_TEMPLATES : DEFAULT_TEMPLATES;


export default function Communications() {
    const [tenants, setTenants] = useState<any[]>([]);
    const [templates] = useState<MessageTemplate[]>(getMarketTemplates(getAppMarket()));
    const [sentMessages, setSentMessages] = useState<SentMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'compose' | 'templates' | 'history' | 'cadence'>('compose');
    const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
    const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
    const [tenantSearch, setTenantSearch] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [messageSubject, setMessageSubject] = useState('');
    const [scheduledAt, setScheduledAt] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [attachmentName, setAttachmentName] = useState('');
    const [attachmentUploading, setAttachmentUploading] = useState(false);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);
    const [cadenceMissed, setCadenceMissed] = useState<any[]>([]);
    const [cadenceLoading, setCadenceLoading] = useState(false);
    const [cadenceError, setCadenceError] = useState<string | null>(null);
    const [cadenceScheduleResult, setCadenceScheduleResult] = useState<string | null>(null);

    const location = useLocation();

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const response = await tenantService.getTenants();
                const fetchedTenants = response.data?.data || response.data || [];
                setTenants(fetchedTenants);

                // Auto-select tenant from navigation state
                const state = location.state as { tenantId?: string };
                if (state?.tenantId) {
                    setSelectedTenants([state.tenantId]);
                }
            } catch (error) {
                console.error("Failed to fetch tenants", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTenants();
    }, [location.state]);


    const handleTemplateSelect = (template: MessageTemplate) => {
        setSelectedTemplate(template);
        setMessageSubject(template.subject || '');
        setMessageContent(template.content);
    };

    const toggleTenant = (id: string) => {
        setSelectedTenants(prev => 
            prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
        );
    };

    const selectGroup = (group: 'all' | 'arrears' | 'upcoming') => {
        if (group === 'all') {
            setSelectedTenants(tenants.map(t => t.id));
        } else if (group === 'arrears') {
            // Mock arrears detection - in production would use backend flags
            setSelectedTenants(tenants.filter((_, i) => i % 5 === 0).map(t => t.id));
        } else if (group === 'upcoming') {
            setSelectedTenants(tenants.filter((_, i) => i % 3 === 0).map(t => t.id));
        }
    };

    const handleSendMessage = async () => {
        if (selectedTenants.length === 0 || !messageContent) {
            alert('Please select at least one recipient and enter a message');
            return;
        }

        setSendingMessage(true);
        let successCount = 0;

        try {
            // Bulk sending logic
            for (const tenantId of selectedTenants) {
                const tenant = tenants.find(t => t.id === tenantId);
                
                // Replace placeholders
                const personalizedContent = messageContent
                    .replace(/{{tenant_name}}/g, tenant?.name || '')
                    .replace(/{{rent_amount}}/g, tenant?.rentAmount || '0.00');

                const response = await communicationService.sendMessage({
                    tenantId: tenantId,
                    subject: messageSubject,
                    content: personalizedContent,
                    type: selectedTemplate?.type === 'sms' ? 'sms' : 'email',
                    scheduledAt: scheduledAt || undefined,
                    attachment: attachmentUrl ? { url: attachmentUrl, name: attachmentName || 'Document' } : undefined,
                });

                if (response.data?.success) {
                    successCount++;
                    const sentMessage: SentMessage = {
                        id: response.data.data?.messageId || `msg-${Date.now()}-${tenantId}`,
                        templateId: selectedTemplate?.id,
                        recipientName: tenant?.name || 'Unknown',
                        recipientEmail: tenant?.email || '',
                        type: selectedTemplate?.type === 'sms' ? 'sms' : 'email',
                        subject: messageSubject,
                        sentAt: response.data.data?.sentAt || new Date().toISOString(),
                        status: 'sent'
                    };
                    setSentMessages(prev => [sentMessage, ...prev]);
                }
            }

            setMessageContent('');
            setMessageSubject('');
            setScheduledAt('');
            setAttachmentUrl('');
            setAttachmentName('');
            setSelectedTemplate(null);
            setSelectedTenants([]);
            alert(`Successfully processed ${successCount} messages! ${scheduledAt ? '(Scheduled)' : ''}`);
        } catch (error: any) {
            console.error("Failed to process bulk messages", error);
            alert('Error processing messages: ' + (error.response?.data?.message || error.message));
        } finally {
            setSendingMessage(false);
        }
    };


    const getCategoryColor = (category: string) => {
        const colors: Record<string, string> = {
            rent: 'bg-blue-100 text-blue-800',
            maintenance: 'bg-yellow-100 text-yellow-800',
            compliance: 'bg-green-100 text-green-800',
            notice: 'bg-red-100 text-red-800',
            general: 'bg-gray-100 text-gray-800'
        };
        return colors[category] || colors.general;
    };

    const getTypeIcon = (type: string) => {
        const icons: Record<string, string> = {
            email: 'fa-envelope',
            sms: 'fa-mobile-alt',
            letter: 'fa-file-alt'
        };
        return icons[type] || 'fa-envelope';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-12 w-12 bg-green-200 rounded-full mb-4"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">

                {/* Header */}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            <i className="fas fa-paper-plane text-green-600 mr-3"></i>
                            Communication Toolkit
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Send messages to tenants using pre-built templates or custom communications.
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('compose')}
                            className={`${activeTab === 'compose' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            <i className="fas fa-edit mr-2"></i>
                            Compose Message
                        </button>
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`${activeTab === 'templates' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            <i className="fas fa-file-alt mr-2"></i>
                            Templates ({templates.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`${activeTab === 'history' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            <i className="fas fa-history mr-2"></i>
                            Message History ({sentMessages.length})
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('cadence');
                                setCadenceError(null);
                                setCadenceMissed([]);
                                setCadenceScheduleResult(null);
                            }}
                            className={`${activeTab === 'cadence' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            <i className="fas fa-clock mr-2"></i>
                            Cadence
                        </button>
                    </nav>
                </div>

                {/* Compose Tab */}
                {activeTab === 'compose' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Sidebar: Recipients & Templates */}
                        <div className="lg:col-span-1 space-y-6">
                            {/* Recipient Selection */}
                            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Recipients</h3>
                                    <span className="text-[10px] font-black text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
                                        {selectedTenants.length} SELECTED
                                    </span>
                                </div>
                                
                                <div className="flex gap-1.5 mb-3">
                                    <button 
                                        onClick={() => selectGroup('all')}
                                        className="flex-1 text-[9px] font-black text-slate-500 bg-slate-50 py-1.5 rounded border border-slate-100 hover:bg-slate-100 transition"
                                    >ALL</button>
                                    <button 
                                        onClick={() => selectGroup('arrears')}
                                        className="flex-1 text-[9px] font-black text-rose-500 bg-rose-50 py-1.5 rounded border border-rose-100 hover:bg-rose-100 transition"
                                    >ARREARS</button>
                                    <button 
                                        onClick={() => selectGroup('upcoming')}
                                        className="flex-1 text-[9px] font-black text-blue-500 bg-blue-50 py-1.5 rounded border border-blue-100 hover:bg-blue-100 transition"
                                    >DUE SOON</button>
                                </div>

                                <div className="relative mb-2">
                                    <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={tenantSearch}
                                        onChange={e => setTenantSearch(e.target.value)}
                                        placeholder="Search name, email or property..."
                                        className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100"
                                    />
                                </div>

                                <div className="max-h-[300px] overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                    {(() => {
                                        const q = tenantSearch.trim().toLowerCase();
                                        const filtered = q
                                            ? tenants.filter(t =>
                                                (t.name || '').toLowerCase().includes(q) ||
                                                (t.email || '').toLowerCase().includes(q) ||
                                                (t.propertyAddress || '').toString().toLowerCase().includes(q) ||
                                                (t.phone || '').includes(q)
                                            )
                                            : tenants;
                                        if (filtered.length === 0) {
                                            return <p className="text-[10px] text-slate-400 text-center py-3">No tenants match "{tenantSearch}"</p>;
                                        }
                                        return filtered.map(tenant => (
                                            <div
                                                key={tenant.id}
                                                onClick={() => toggleTenant(tenant.id)}
                                                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                                                    selectedTenants.includes(tenant.id) ? 'bg-emerald-50 border border-emerald-100' : 'hover:bg-slate-50 border border-transparent'
                                                }`}
                                            >
                                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition ${
                                                    selectedTenants.includes(tenant.id) ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'
                                                }`}>
                                                    {selectedTenants.includes(tenant.id) && <i className="fas fa-check text-[8px]"></i>}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-[11px] font-black text-slate-900 truncate uppercase">{tenant.name}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 truncate -mt-0.5">{tenant.email}</p>
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>

                            {/* Templates */}
                            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-4">Templates</h3>
                                <div className="space-y-2">
                                    {templates.map(template => (
                                        <button
                                            key={template.id}
                                            onClick={() => handleTemplateSelect(template)}
                                            className={`w-full text-left p-2.5 rounded-lg border transition ${selectedTemplate?.id === template.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-100 hover:border-slate-300'}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-[11px] font-black text-slate-900 uppercase tracking-tighter">{template.name}</span>
                                                <i className={`fas ${getTypeIcon(template.type)} text-[10px] text-slate-400`}></i>
                                            </div>
                                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase ${getCategoryColor(template.category)}`}>
                                                {template.category}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Compose Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full flex flex-col">
                                <div className="space-y-4 flex-1">
                                    {/* Subject */}
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Subject</label>
                                        <input
                                            type="text"
                                            value={messageSubject}
                                            onChange={(e) => setMessageSubject(e.target.value)}
                                            placeholder="Enter communication subject..."
                                            className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-lg text-sm font-bold text-slate-900 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                        />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 flex flex-col min-h-[300px]">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Communication Body</label>
                                        <textarea
                                            value={messageContent}
                                            onChange={(e) => setMessageContent(e.target.value)}
                                            className="flex-1 w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm font-bold text-slate-900 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                                            placeholder="Draft your message here. Use templates for automated placeholders..."
                                        />
                                        <div className="flex items-center gap-2 mt-2 px-1">
                                            <i className="fas fa-info-circle text-slate-300 text-[10px]"></i>
                                            <p className="text-[10px] font-bold text-slate-400">
                                                Available Placeholders: {'{{tenant_name}}'}, {'{{rent_amount}}'}, {'{{property_address}}'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Attachment — pick a file, browser uploads it directly to S3
                                        and the returned presigned read URL is stamped into the
                                        outgoing email so Brevo/SMTP can attach it at send time. */}
                                    <div className="pt-4 border-t border-slate-50">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Attachment (optional)</label>
                                        {attachmentUrl ? (
                                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <i className="fas fa-paperclip text-emerald-600 text-xs"></i>
                                                    <span className="text-xs font-bold text-slate-900 truncate">{attachmentName || 'Attached file'}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => { setAttachmentUrl(''); setAttachmentName(''); setAttachmentError(null); }}
                                                    className="text-[10px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-widest"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <label className={`flex items-center gap-2 px-3 py-2 bg-slate-50 border border-dashed border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition ${attachmentUploading ? 'opacity-60 cursor-wait' : ''}`}>
                                                <i className={`fas ${attachmentUploading ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'} text-slate-400 text-xs`}></i>
                                                <span className="text-xs font-bold text-slate-500">
                                                    {attachmentUploading ? 'Uploading...' : 'Choose file to attach'}
                                                </span>
                                                <input
                                                    type="file"
                                                    accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
                                                    disabled={attachmentUploading}
                                                    className="hidden"
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (!file) return;
                                                        if (file.size > 10 * 1024 * 1024) {
                                                            setAttachmentError('File too large (max 10 MB).');
                                                            e.target.value = '';
                                                            return;
                                                        }
                                                        setAttachmentError(null);
                                                        setAttachmentUploading(true);
                                                        try {
                                                            const pres = await communicationService.getAttachmentPresignedUrl({ fileName: file.name, fileType: file.type || 'application/octet-stream' });
                                                            const { uploadUrl, readUrl } = pres.data?.data || {};
                                                            if (!uploadUrl || !readUrl) throw new Error('Failed to get upload URL');
                                                            const putRes = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
                                                            if (!putRes.ok) throw new Error('Upload to S3 failed');
                                                            setAttachmentUrl(readUrl);
                                                            setAttachmentName(file.name);
                                                        } catch (err: any) {
                                                            setAttachmentError(err.message || 'Upload failed');
                                                        } finally {
                                                            setAttachmentUploading(false);
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                />
                                            </label>
                                        )}
                                        {attachmentError && (
                                            <p className="mt-1 text-[10px] font-bold text-rose-500">{attachmentError}</p>
                                        )}
                                    </div>

                                    {/* Scheduling */}
                                    <div className="pt-4 border-t border-slate-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                            <div className="flex items-center gap-2">
                                                <i className="fas fa-calendar-alt text-slate-400 text-xs"></i>
                                                <span className="text-[10px] font-black text-slate-500 uppercase">Schedule Send</span>
                                            </div>
                                            <input 
                                                type="datetime-local" 
                                                value={scheduledAt}
                                                onChange={(e) => setScheduledAt(e.target.value)}
                                                className="bg-transparent border-none text-[11px] font-black text-slate-900 focus:ring-0 cursor-pointer"
                                            />
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => {
                                                    setMessageContent('');
                                                    setMessageSubject('');
                                                    setSelectedTemplate(null);
                                                    setSelectedTenants([]);
                                                    setScheduledAt('');
                                                    setAttachmentUrl('');
                                                    setAttachmentName('');
                                                }}
                                                className="text-[10px] font-black text-slate-400 hover:text-rose-500 uppercase tracking-widest transition-colors px-4"
                                            >
                                                Discard
                                            </button>
                                            <button
                                                onClick={handleSendMessage}
                                                disabled={sendingMessage || selectedTenants.length === 0 || !messageContent}
                                                className="px-8 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-20 disabled:grayscale transition-all shadow-lg flex items-center gap-3"
                                            >
                                                {sendingMessage ? (
                                                    <i className="fas fa-spinner fa-spin text-xs"></i>
                                                ) : (
                                                    <i className="fas fa-paper-plane text-xs opacity-50"></i>
                                                )}
                                                <span className="text-[11px] font-black uppercase tracking-widest">
                                                    {scheduledAt ? 'Schedule Send' : `Transmit to ${selectedTenants.length} Units`}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* Templates Tab */}
                {activeTab === 'templates' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {templates.map(template => (
                            <div key={template.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(template.category)}`}>
                                            {template.category}
                                        </span>
                                    </div>
                                    <i className={`fas ${getTypeIcon(template.type)} text-gray-400 text-lg`}></i>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                                    {template.content.slice(0, 150)}...
                                </p>
                                <button
                                    onClick={() => {
                                        handleTemplateSelect(template);
                                        setActiveTab('compose');
                                    }}
                                    className="w-full px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium"
                                >
                                    Use Template
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {sentMessages.length === 0 ? (
                            <div className="text-center py-12">
                                <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                                <p className="text-gray-500">No messages sent yet.</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sentMessages.map(message => (
                                        <tr key={message.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{message.recipientName}</div>
                                                <div className="text-sm text-gray-500">{message.recipientEmail}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.subject}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <i className={`fas ${getTypeIcon(message.type)} text-gray-400`}></i>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(message.sentAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${message.status === 'sent' ? 'bg-green-100 text-green-800' : message.status === 'delivered' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                                                    {message.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* Cadence Tab */}
                {activeTab === 'cadence' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900">Missed-cadence scan</h3>
                                    <p className="text-xs text-gray-500">
                                        Vulnerability-aware communication checks that are overdue, based on current message evidence.
                                    </p>
                                </div>
                                <button
                                    onClick={async () => {
                                        setCadenceLoading(true);
                                        setCadenceError(null);
                                        try {
                                            const res = await communicationService.getMissedCadence();
                                            setCadenceMissed(res.data?.data?.missed || []);
                                        } catch (err: any) {
                                            setCadenceError(err?.response?.data?.message || err.message || 'Failed to load cadence scan');
                                        } finally {
                                            setCadenceLoading(false);
                                        }
                                    }}
                                    className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest"
                                >
                                    Run Scan
                                </button>
                            </div>

                            {cadenceError && <p className="text-xs font-bold text-rose-600">{cadenceError}</p>}

                            {!cadenceLoading && cadenceMissed.length === 0 && (
                                <p className="text-xs text-gray-500">No missed-cadence items found yet. Run a scan to check current portfolio state.</p>
                            )}

                            <div className="mt-4 space-y-2">
                                {cadenceMissed.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 bg-slate-50 p-3">
                                        <div>
                                            <p className="text-xs font-black text-slate-900">{item.tenantId}</p>
                                            <p className="text-[11px] font-bold text-slate-500">
                                                {item.profile} · {item.frequency} · {item.primaryMethod}
                                            </p>
                                            <p className="text-[10px] text-slate-500">
                                                Due: {item.nextContactAt ? new Date(item.nextContactAt).toLocaleString() : '-'} · {item.sinceDays ?? 0} days overdue
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={async () => {
                                                    setCadenceScheduleResult(null);
                                                    try {
                                                        const res = await communicationService.scheduleTenantCadence(item.tenantId, 14);
                                                        setCadenceScheduleResult(`Scheduled ${res.data?.count ?? 0} cadence events for ${item.tenantId}`);
                                                    } catch (err: any) {
                                                        setCadenceScheduleResult(`Schedule failed: ${err?.response?.data?.message || err.message || 'unknown error'}`);
                                                    }
                                                }}
                                                className="px-2 py-1.5 bg-emerald-600 text-white rounded text-[10px] font-black uppercase"
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                onClick={() => { setSelectedTenants([item.tenantId]); setActiveTab('compose'); }}
                                                className="px-2 py-1.5 bg-slate-900 text-white rounded text-[10px] font-black uppercase"
                                            >
                                                Contact
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {cadenceScheduleResult && (
                                <p className="mt-3 text-[11px] font-bold text-slate-800">{cadenceScheduleResult}</p>
                            )}
                        </div>
                    </div>
                )}

            </div>
            <Footer />
        </div>
    );
}
